import { useEffect, useState } from 'react'

import { useSavedFilter } from './useSavedFilter'
import { useSelector } from 'react-redux'

import {
  useDevicesFormOptionsLazyQuery,
  useFacilityNamesLazyQuery,
  useFloorsByFacilityIdsLazyQuery,
  useIncidentFiltersEnrichmentQuery,
} from '@/graphql/generated/hooks'
import { buildFormState } from '@/redux/incidents/conversion'
import {
  aiOperatorStateOptions,
  clipsOptions,
  createdAtIntervalOptions,
  incidentStatusOptions,
  mutedOptions,
  priorityOptions,
  sourceTypeOptions,
} from '@/redux/incidents/incidentsFilterOptions'
import { selectActiveFilterCriteria } from '@/redux/incidents/incidentsSlice'
import {
  OptionI,
  Options,
  getDeviceOptions,
  getFacilityOptions,
  getFloorOptions,
  getIncidentTypeOptions,
} from '@/utils/forms/optionBuilders'

import { SavedFilterI } from '../types/generic'
import {
  areAllDefined,
  dummyDeviceId,
  hasFacilitiesOrFloors,
} from '../utils/utils'

interface UseIncidentFilterFormValuesIProps {
  watch: any
  editedFilter: SavedFilterI | null
}

const DEVICE_MIN_SEARCH_LENGTH = 3
const FACILITY_MIN_SEARCH_LENGTH = 1

export const useIncidentFilterFormValues = ({
  watch,
  editedFilter,
}: UseIncidentFilterFormValuesIProps) => {
  // In regular form state this retrieves criteria saved in Redux state
  // In update filter form this mimics filter state for selected filter
  const criteria = editedFilter
    ? useSavedFilter({ savedFilter: editedFilter })
    : useSelector(selectActiveFilterCriteria)

  const { data: enrichmentData } = useIncidentFiltersEnrichmentQuery({
    // For the time being we always want to hide archived results.
    // Maybe in future we can open this up to being a toggle within the search?
    variables: {
      includeArchived: false,
      // This hack is used to bypass the filtering issue
      // When empty array is used as for deviceIdIn all devices are returned instead of no devices
      deviceIdIn: criteria?.deviceIdIn ?? [dummyDeviceId],
      facilityIdIn: criteria?.facilityIdIn ?? [],
    },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    skip: !criteria,
  })

  const [floorFormOptions, setFloorFormOptions] = useState<Options>()
  const [deviceFormOptions, setDeviceFormOptions] = useState<Options>()
  const [facilityFormOptions, setFacilityFormOptions] = useState<Options>()
  const [deviceSearchValue, setDeviceSearchValue] = useState<string>('')
  const [facilitySearchValue, setFacilitySearchValue] = useState<string>('')

  const [getFloorFormOptions, { loading: isFloorLoading }] =
    useFloorsByFacilityIdsLazyQuery()

  const [getDeviceFormOptions, { loading: isDeviceLoading }] =
    useDevicesFormOptionsLazyQuery()

  const [getFacilityFormOptions, { loading: isFacilityLoading }] =
    useFacilityNamesLazyQuery()

  const isLoading = !criteria || !enrichmentData
  const watchFacility: OptionI[] = watch('facilityName', criteria?.facilityIdIn)
  const watchFloor: OptionI[] = watch('floorName')

  useEffect(() => {
    if (areAllDefined(watchFacility, watchFloor)) {
      if (watchFacility?.[0]?.value) {
        getFloorFormOptions({
          variables: { ids: watchFacility?.map((e) => e.value) },
          onCompleted: (floorsData) => {
            setFloorFormOptions(getFloorOptions(floorsData))
          },
        })
      } else {
        setFloorFormOptions([])
      }
    }
  }, [watchFacility])

  useEffect(() => {
    const shouldUseFacilitySearch =
      facilitySearchValue.length >= FACILITY_MIN_SEARCH_LENGTH

    if (!shouldUseFacilitySearch) {
      setFacilityFormOptions([])
      return
    }

    getFacilityFormOptions({
      variables: {
        filter: { nameLike: facilitySearchValue },
      },
      onCompleted: (facilitiesData) => {
        setFacilityFormOptions(getFacilityOptions(facilitiesData))
      },
    })
  }, [facilitySearchValue])

  useEffect(() => {
    // If facility or floor is available fetch all devices for that facility and/or floor
    const shouldUseFacilitiesOrFloors =
      hasFacilitiesOrFloors(watchFacility, watchFloor) &&
      areAllDefined(watchFacility, watchFloor)
    // Otherwise use the search term if it has more than three characters
    const shouldUseSearchValue =
      !shouldUseFacilitiesOrFloors &&
      deviceSearchValue.length >= DEVICE_MIN_SEARCH_LENGTH

    if (shouldUseFacilitiesOrFloors || shouldUseSearchValue) {
      getDeviceFormOptions({
        variables: {
          filter: {
            ...(shouldUseFacilitiesOrFloors && {
              facilityIdIn: watchFacility?.map((f) => f.value) ?? [],
            }),
            ...(shouldUseFacilitiesOrFloors && {
              floorIdIn: watchFloor?.map((f) => f.value) ?? [],
            }),
            ...(shouldUseSearchValue && { nameLike: deviceSearchValue }),
          },
        },
        onCompleted: (devicesData) => {
          setDeviceFormOptions(getDeviceOptions(devicesData))
        },
      })
    } else {
      // Don't show any options
      setDeviceFormOptions([])
    }
  }, [watchFacility, watchFloor, deviceSearchValue])

  const options = {
    createdAtOptions: createdAtIntervalOptions,
    deviceNameOptions: deviceFormOptions,
    facilityNameOptions:
      facilitySearchValue.length >= FACILITY_MIN_SEARCH_LENGTH
        ? facilityFormOptions
        : getFacilityOptions(enrichmentData),
    floorNameOptions: floorFormOptions,
    incidentStatusOptions: incidentStatusOptions,
    incidentTypeOptions: getIncidentTypeOptions(enrichmentData),
    mutedOptions: mutedOptions,
    priorityOptions: priorityOptions,
    sourceTypeOptions: sourceTypeOptions,
    clipsOptions: clipsOptions,
    aiOperatorStateOptions: aiOperatorStateOptions,
  }

  const defaultValues = buildFormState(criteria, enrichmentData)

  if (isLoading) {
    return {
      defaultValues: null,
      options: null,
      deviceSearchValue: null,
      onDeviceSearchChange: null,
      facilitySearchValue: null,
      onFacilitySearchChange: null,
      isDeviceLoading: null,
      isFloorLoading: null,
      isFacilityLoading: null,
    }
  }

  return {
    defaultValues,
    options,
    deviceSearchValue,
    onDeviceSearchChange: hasFacilitiesOrFloors(watchFacility, watchFloor)
      ? null
      : setDeviceSearchValue,
    facilitySearchValue,
    onFacilitySearchChange: setFacilitySearchValue,
    isDeviceLoading,
    isFloorLoading,
    isFacilityLoading,
  }
}
